import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import {
  Button,
  ColorPalette,
  ConfirmDialog,
  ErrorNote,
  Field,
  Loading,
  Modal,
  Tabs,
  Toggle,
  inputClass,
} from '../../components/ui';
import { errorMessage } from '../../lib/api';
import { useAuth } from '../../lib/auth-context-value';
import { GROUP_PALETTE } from '../../lib/palette';
import { customFieldGroups, customFields as customFieldsApi } from '../../lib/resources';
import type {
  CustomField,
  CustomFieldEntity,
  CustomFieldGroup,
  CustomFieldType,
} from '../../types';
import { CustomFieldForm } from './CustomFieldForm';

const ENTITY_TABS: Array<[CustomFieldEntity, string]> = [
  ['lead', 'Lead (Pessoa)'],
  ['company', 'Lead (Empresa)'],
  ['business', 'Negócio'],
];

const TYPE_CHIP: Record<CustomFieldType, [string, string]> = {
  TEXT: ['Texto', 'bg-sky-50 text-sky-700'],
  LONG_TEXT: ['Texto longo', 'bg-sky-50 text-sky-700'],
  PHONE: ['Telefone', 'bg-sky-50 text-sky-700'],
  EMAIL: ['E-mail', 'bg-sky-50 text-sky-700'],
  URL: ['URL', 'bg-sky-50 text-sky-700'],
  NUMBER: ['Número', 'bg-emerald-50 text-emerald-700'],
  DATE: ['Data', 'bg-amber-50 text-amber-700'],
  BOOLEAN: ['Booleano', 'bg-slate-100 text-slate-600'],
  SELECT: ['Lista', 'bg-violet-50 text-violet-700'],
  MULTI_SELECT: ['Lista múltipla', 'bg-violet-50 text-violet-700'],
  RATING: ['Avaliação', 'bg-rose-50 text-rose-700'],
};

/** "Sem grupo" no arrastar e no acordeão. */
const NO_GROUP = '__sem_grupo__';

/**
 * Configurações › Campos adicionais: abas por entidade, grupos em acordeão e
 * ordem por arrastar (dentro do grupo e entre grupos).
 */
export function CustomFieldsSection() {
  const queryClient = useQueryClient();
  const canWrite = useAuth().can('aux_admin');
  const [entity, setEntity] = useState<CustomFieldEntity>('lead');
  const [search, setSearch] = useState('');
  const [collapsed, setCollapsed] = useState<string[]>([]);
  const [editingField, setEditingField] = useState<{
    field: CustomField | null;
    groupId?: string | null;
  } | null>(null);
  const [editingGroup, setEditingGroup] = useState<CustomFieldGroup | 'new' | null>(null);
  const [confirm, setConfirm] = useState<
    { kind: 'field'; field: CustomField } | { kind: 'group'; group: CustomFieldGroup } | null
  >(null);
  const [dragging, setDragging] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<string | null>(null);

  const fieldsQuery = useQuery({
    queryKey: ['custom-fields', entity],
    queryFn: () => customFieldsApi.list({ entity }),
  });
  const groupsQuery = useQuery({
    queryKey: ['custom-field-groups', entity],
    queryFn: () => customFieldGroups.list(entity),
  });
  const groups = useMemo(() => groupsQuery.data?.data ?? [], [groupsQuery.data]);
  const fields = useMemo(() => fieldsQuery.data?.data ?? [], [fieldsQuery.data]);

  /** Campo de grupo excluído aparece em "Sem grupo" — volta ao grupo se ele for restaurado. */
  const groupKeyOf = (field: CustomField) =>
    field.groupId && groups.some((group) => group.id === field.groupId)
      ? field.groupId
      : NO_GROUP;

  const buckets = useMemo(() => {
    const term = search.trim().toLowerCase();
    const visible = term
      ? fields.filter((field) => field.name.toLowerCase().includes(term))
      : fields;
    const map = new Map<string, CustomField[]>([[NO_GROUP, []]]);
    groups.forEach((group) => map.set(group.id, []));
    visible.forEach((field) => map.get(groupKeyOf(field))?.push(field));
    return map;
    // groupKeyOf depende só de `groups`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields, groups, search]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['custom-fields'] });
    queryClient.invalidateQueries({ queryKey: ['custom-field-groups'] });
    queryClient.invalidateQueries({ queryKey: ['trash'] });
  };

  const reorder = useMutation({
    mutationFn: customFieldsApi.reorder,
    onSettled: invalidate,
  });

  const remove = useMutation({
    mutationFn: async () => {
      if (confirm?.kind === 'field') await customFieldsApi.remove(confirm.field.id);
      if (confirm?.kind === 'group') await customFieldGroups.remove(confirm.group.id);
    },
    onSuccess: () => {
      setConfirm(null);
      invalidate();
    },
  });

  /** Solta `dragging` antes de `beforeId` (ou no fim do grupo) e renumera os grupos afetados. */
  const drop = (groupKey: string, beforeId: string | null) => {
    const moving = fields.find((field) => field.id === dragging);
    setDragging(null);
    setDropTarget(null);
    if (!moving || moving.id === beforeId) return;

    const sourceKey = groupKeyOf(moving);
    const ordered = (key: string) =>
      fields.filter((field) => groupKeyOf(field) === key && field.id !== moving.id);
    const target = ordered(groupKey);
    const index = beforeId ? target.findIndex((field) => field.id === beforeId) : -1;
    target.splice(index >= 0 ? index : target.length, 0, moving);

    const items = target.map((field, position) => ({
      id: field.id,
      groupId: groupKey === NO_GROUP ? null : groupKey,
      position,
    }));
    if (sourceKey !== groupKey) {
      ordered(sourceKey).forEach((field, position) =>
        items.push({
          id: field.id,
          groupId: sourceKey === NO_GROUP ? null : sourceKey,
          position,
        }),
      );
    }

    // Otimista: a lista já mostra a ordem nova enquanto a API grava.
    queryClient.setQueryData(['custom-fields', entity], {
      count: fields.length,
      data: fields
        .map((field) => {
          const changed = items.find((item) => item.id === field.id);
          return changed
            ? { ...field, groupId: changed.groupId, position: changed.position }
            : field;
        })
        .sort((a, b) => a.position - b.position),
    });
    reorder.mutate(items);
  };

  const sections: Array<{ key: string; group: CustomFieldGroup | null }> = [
    ...groups.map((group) => ({ key: group.id, group })),
    { key: NO_GROUP, group: null },
  ];

  return (
    <section className="rounded-xl border border-slate-200 bg-white">
      <header className="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-4">
        <div>
          <h2 className="text-base font-semibold text-slate-900">Campos adicionais</h2>
          <p className="text-xs text-slate-500">
            Campos customizados por entidade, com grupos, tipo, validação e ordem.
          </p>
        </div>
        {canWrite && (
          <div className="flex gap-2">
            <Button onClick={() => setEditingGroup('new')}>Novo grupo</Button>
            <Button variant="primary" onClick={() => setEditingField({ field: null })}>
              + Novo campo
            </Button>
          </div>
        )}
      </header>

      <div className="px-5 pt-2">
        <Tabs tabs={ENTITY_TABS} value={entity} onChange={setEntity} />
      </div>

      <div className="flex items-center gap-3 px-5 py-3">
        <input
          className={`${inputClass} mt-0 max-w-xs`}
          placeholder="Buscar campo"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <span className="text-xs text-slate-500">{fields.length} campo(s)</span>
        {reorder.error && (
          <ErrorNote>{errorMessage(reorder.error, 'A ordem não foi salva')}</ErrorNote>
        )}
      </div>

      {fieldsQuery.isLoading || groupsQuery.isLoading ? (
        <Loading />
      ) : (
        <div className="space-y-3 px-5 pb-5">
          {sections.map(({ key, group }) => {
            const items = buckets.get(key) ?? [];
            const isCollapsed = collapsed.includes(key);
            return (
              <div
                key={key}
                className={`rounded-lg border ${
                  dropTarget === `${key}:end` ? 'border-slate-900' : 'border-slate-200'
                }`}
                onDragOver={(event) => {
                  if (!dragging) return;
                  event.preventDefault();
                  setDropTarget((current) =>
                    current?.startsWith(`${key}:`) ? current : `${key}:end`,
                  );
                }}
                onDrop={(event) => {
                  event.preventDefault();
                  if (dropTarget === `${key}:end`) drop(key, null);
                }}
              >
                <div className="flex items-center gap-2 bg-slate-50 px-3 py-2">
                  <button
                    type="button"
                    className="text-xs text-slate-400"
                    aria-label={isCollapsed ? 'Expandir grupo' : 'Recolher grupo'}
                    onClick={() =>
                      setCollapsed((current) =>
                        current.includes(key)
                          ? current.filter((item) => item !== key)
                          : [...current, key],
                      )
                    }
                  >
                    {isCollapsed ? '▸' : '▾'}
                  </button>
                  {group?.color && (
                    <span
                      className="h-2.5 w-2.5 rounded-full"
                      style={{ backgroundColor: group.color }}
                    />
                  )}
                  <span className="text-sm font-medium text-slate-700">
                    {group?.name ?? 'Sem grupo'}
                  </span>
                  <span className="text-xs text-slate-400">{items.length}</span>
                  {group?.adminOnly && (
                    <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] text-amber-700">
                      Somente administradores
                    </span>
                  )}
                  {group && group.columns === 2 && (
                    <span className="text-[11px] text-slate-400">2 colunas</span>
                  )}
                  {canWrite && (
                    <span className="ml-auto flex gap-3 text-xs">
                      <button
                        type="button"
                        className="text-slate-500 hover:text-slate-900"
                        onClick={() =>
                          setEditingField({ field: null, groupId: group?.id ?? null })
                        }
                      >
                        + Campo
                      </button>
                      {group && (
                        <>
                          <button
                            type="button"
                            className="text-slate-500 hover:text-slate-900"
                            onClick={() => setEditingGroup(group)}
                          >
                            Editar
                          </button>
                          <button
                            type="button"
                            className="text-slate-400 hover:text-red-600"
                            onClick={() => setConfirm({ kind: 'group', group })}
                          >
                            Excluir
                          </button>
                        </>
                      )}
                    </span>
                  )}
                </div>

                {!isCollapsed && (
                  <ul className="divide-y divide-slate-100">
                    {items.length === 0 && (
                      <li className="px-4 py-3 text-xs text-slate-400">
                        {canWrite ? 'Arraste campos para cá ou crie um novo.' : 'Nenhum campo.'}
                      </li>
                    )}
                    {items.map((field) => (
                      <li
                        key={field.id}
                        draggable={canWrite && !search}
                        onDragStart={(event) => {
                          event.dataTransfer.effectAllowed = 'move';
                          setDragging(field.id);
                        }}
                        onDragEnd={() => {
                          setDragging(null);
                          setDropTarget(null);
                        }}
                        onDragOver={(event) => {
                          if (!dragging) return;
                          event.preventDefault();
                          event.stopPropagation();
                          setDropTarget(`${key}:${field.id}`);
                        }}
                        onDrop={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          drop(key, field.id);
                        }}
                        className={`flex items-center gap-3 px-3 py-2.5 text-sm ${
                          dragging === field.id ? 'opacity-40' : ''
                        } ${dropTarget === `${key}:${field.id}` ? 'border-t-2 border-slate-900' : ''}`}
                      >
                        {canWrite && (
                          <span
                            className="cursor-grab select-none text-slate-300"
                            aria-hidden
                            title="Arraste para reordenar"
                          >
                            ⋮⋮
                          </span>
                        )}
                        <span className="min-w-0 flex-1">
                          <span className="font-medium text-slate-800">{field.name}</span>
                          {field.alsoCompany && (
                            <span
                              className="ml-2 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-500"
                              title="Vale para Pessoa e Empresa"
                            >
                              Pessoa + Empresa
                            </span>
                          )}
                          {!field.isPublic && (
                            <span
                              className="ml-2 text-[10px] text-slate-400"
                              title="Só administradores veem"
                            >
                              🔒
                            </span>
                          )}
                        </span>
                        <span
                          className={`rounded-full px-2 py-0.5 text-xs ${TYPE_CHIP[field.type][1]}`}
                        >
                          {TYPE_CHIP[field.type][0]}
                        </span>
                        <span
                          className="w-24 text-center text-xs text-slate-500"
                          title="Sempre visível"
                        >
                          {field.alwaysVisible ? '✓ visível' : ''}
                        </span>
                        {canWrite && (
                          <span className="flex gap-3 text-xs">
                            <button
                              type="button"
                              className="text-slate-500 hover:text-slate-900"
                              onClick={() => setEditingField({ field })}
                            >
                              Editar
                            </button>
                            <button
                              type="button"
                              className="text-slate-400 hover:text-red-600"
                              onClick={() => setConfirm({ kind: 'field', field })}
                            >
                              Excluir
                            </button>
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      )}

      {editingField && (
        <CustomFieldForm
          entity={entity}
          field={editingField.field}
          groups={groups}
          defaultGroupId={editingField.groupId}
          onClose={() => setEditingField(null)}
        />
      )}
      {editingGroup && (
        <GroupModal
          entity={entity}
          group={editingGroup === 'new' ? null : editingGroup}
          onClose={() => setEditingGroup(null)}
          onSaved={invalidate}
        />
      )}
      {confirm && (
        <ConfirmDialog
          title={confirm.kind === 'field' ? 'Excluir campo?' : 'Excluir grupo?'}
          message={
            confirm.kind === 'field' ? (
              <p>
                <strong>{confirm.field.name}</strong> vai para a Lixeira, com os valores
                preenchidos. Dá para restaurar por 30 dias.
              </p>
            ) : (
              <p>
                <strong>{confirm.group.name}</strong> vai para a Lixeira. Os campos dele passam
                a aparecer em "Sem grupo" e voltam para o grupo se ele for restaurado.
              </p>
            )
          }
          confirmLabel="Excluir"
          danger
          busy={remove.isPending}
          error={remove.error ? errorMessage(remove.error) : null}
          onConfirm={() => remove.mutate()}
          onClose={() => setConfirm(null)}
        />
      )}
    </section>
  );
}

/** "Novo grupo": nome, cor (paleta menor), disposição e "Somente administradores". */
function GroupModal({
  entity,
  group,
  onClose,
  onSaved,
}: {
  entity: CustomFieldEntity;
  group: CustomFieldGroup | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(group?.name ?? '');
  const [color, setColor] = useState<string | null>(group?.color ?? GROUP_PALETTE[0]);
  const [columns, setColumns] = useState<1 | 2>(group?.columns ?? 1);
  const [adminOnly, setAdminOnly] = useState(group?.adminOnly ?? false);

  const save = useMutation({
    mutationFn: () => {
      const payload = { name: name.trim(), color: color ?? undefined, columns, adminOnly };
      return group
        ? customFieldGroups.update(group.id, payload)
        : customFieldGroups.create({ ...payload, entity });
    },
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });

  return (
    <Modal title={group ? 'Editar grupo' : 'Novo grupo'} onClose={onClose}>
      <form
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          save.mutate();
        }}
      >
        <Field label="Nome do grupo *">
          <input
            required
            autoFocus
            className={inputClass}
            placeholder="Informações comerciais"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </Field>
        <div className="text-sm">
          <span className="font-medium text-slate-700">Cor</span>
          <ColorPalette value={color} onChange={setColor} colors={GROUP_PALETTE} />
        </div>
        <Field label="Disposição dos campos" hint="Em telas menores, uma coluna.">
          <select
            className={inputClass}
            value={columns}
            onChange={(event) => setColumns(Number(event.target.value) as 1 | 2)}
          >
            <option value={1}>1 coluna</option>
            <option value={2}>2 colunas</option>
          </select>
        </Field>
        <Toggle
          label="Somente administradores"
          hint="Só administradores veem e editam os campos do grupo."
          checked={adminOnly}
          onChange={setAdminOnly}
        />
        {save.error && (
          <ErrorNote>{errorMessage(save.error, 'Não foi possível salvar')}</ErrorNote>
        )}
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Cancelar</Button>
          <Button type="submit" variant="primary" disabled={save.isPending}>
            {save.isPending ? 'Salvando…' : 'Salvar grupo'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
